// ============================================================
// boton-estilo.js — el estilo de un botón puesto a mano, en TRES niveles
// (plan de personalización manual, 16-10-2026, §3.3; decisiones D2, D6 y D7 del dueño)
//
// Módulo PURO (sin DOM ni `window`; ES2019, sin `?.` ni `??`). Lo importan los renderers de los
// bloques con botón (bundle público incluido), `core/render.js` y `pages/viewer.js`.
//
// LOS TRES NIVELES (D2: si se combinan, gana el más concreto, propiedad a propiedad):
//   · toda la web  → `site_tokens.boton`;
//   · la sección   → `props.boton` del bloque;
//   · el botón     → `botones[<campo>]` del objeto DUEÑO del campo: `props.botones.cta` en un bloque,
//                    `plans[i].botones.cta` / `slides[i].botones.cta` en una tarjeta. Vive en el item y
//                    no en una ruta con índice para que viaje con la tarjeta al reordenar y para que
//                    la fusión de la IA (`ai-merge.js`) lo conserve por IDENTIDAD.
// Un estilo es `{bg, fg, borde, borde_color, radio, tamano}`, todo opcional (`normalizarEstilo`).
//
// CÓMO SE PINTA. Variables propias `--n3b-*` que leen las reglas del PROPIO `.btn-action`
// (`site.css`: `var(--n3b-bg, var(--btn-bg, …))`). No se reutilizan las `--btn-*` a propósito:
// `[data-btn-style]` y los presets las declaran EN CADA <section>, y un `var()` dentro de una custom
// property se resuelve donde se declara, así que lo de toda la web perdería frente a ellas y lo del
// botón no podría tocarlas. Como nadie más declara `--n3b-*`:
//   · lo de la WEB se declara una vez arriba y se hereda (`:root` del publicado en compileSiteHtml;
//     `#resultado` y la cabecera izada en el editor, `aplicarBotonWeb` vía `applyTokensOnly`);
//   · lo de la SECCIÓN y del BOTÓN lo escribe el renderer EN el propio `<a>`/`<button>`
//     (`estiloDeBoton`): paridad editor/publicado gratis, porque `compileSiteHtml` llama a los mismos
//     renderers (gotcha 12), sin inyección nueva sobre el <section>.
// SIN NADA puesto, ninguna función emite nada: lo publicado sale byte a byte como antes.
//
// SEGURIDAD: todo valor acaba en un `style=` (o en el `<style>` del head). Solo pasan hex literales,
// enteros acotados y enums; lo demás se descarta (gotcha 35c). Espejo de la frontera de escritura en
// `views.py::_sanear_estilo_boton` (mismas reglas; lo compara tests/test_boton_estilo.py).
// ============================================================

import { hexToRgb, getLuminance } from './contrast.js';

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Grosor del borde (px) y redondeo (px). `RADIO_PILDORA` = la píldora («Píldora» del panel). */
export const BORDE_MAX = 8;
export const RADIO_MAX = 60;
export const RADIO_PILDORA = 999;
/** Tamaño → factor de la letra y del relleno (`--n3b-k`). `normal` explícito emite 1: así un botón en
 *  «Normal» pisa un «Grande» de toda la web (gana el más concreto). Sin tamaño, nada. */
export const TAMANOS = { pequeno: 0.85, normal: 1, grande: 1.2 };
/** Las claves de un estilo, en el orden en que se emiten. */
export const CLAVES_ESTILO = ['bg', 'fg', 'borde', 'borde_color', 'radio', 'tamano'];
/** Todas las variables que se pueden emitir (para quitarlas en el editor). */
export const VARIABLES_BOTON = ['--n3b-bg', '--n3b-fg', '--n3b-shadow', '--n3b-border', '--n3b-radius', '--n3b-k'];
/**
 * Los campos del BOTÓN SECUNDARIO (utils/boton-secundario.js). Su aspecto es «un peso menos» que el
 * principal (contorno del color del texto, D-1200), así que de lo puesto para TODA la sección o TODA la
 * web solo recibe la FORMA (`CLAVES_DE_FORMA`): con el fondo de los botones de la sección sería un segundo
 * botón principal. Lo puesto en él mismo («Este botón») sí lo cambia todo. Lo de toda la web lo corta
 * site.css (`.btn-secundario` anula las `--n3b-*` de color heredadas).
 */
export const CAMPOS_SECUNDARIOS = ['cta2'];
export const CLAVES_DE_FORMA = ['radio', 'tamano'];

function esObjetoPlano(v) {
    return !!v && typeof v === 'object' && !Array.isArray(v);
}

function hex(v) {
    if (typeof v !== 'string') return '';
    const s = v.trim().toLowerCase();
    return HEX_RE.test(s) ? s : '';
}

/** Entero dentro de [min, max], o null. Acepta números y cadenas de dígitos (lo que da un input). */
function entero(v, min, max) {
    if (typeof v === 'string' && /^\s*\d{1,4}\s*$/.test(v)) v = parseInt(v, 10);
    if (typeof v !== 'number' || !isFinite(v)) return null;
    const n = Math.round(v);
    return n < min || n > max ? null : n;
}

/**
 * El estilo LIMPIO: solo las claves conocidas con valores válidos. `null` si no queda ninguna (así un
 * estilo vacío o roto no emite nada ni se guarda).
 */
export function normalizarEstilo(estilo) {
    if (!esObjetoPlano(estilo)) return null;
    const out = {};
    const bg = hex(estilo.bg);
    if (bg) out.bg = bg;
    const fg = hex(estilo.fg);
    if (fg) out.fg = fg;
    const borde = entero(estilo.borde, 0, BORDE_MAX);
    if (borde !== null) out.borde = borde;
    const bc = hex(estilo.borde_color);
    if (bc) out.borde_color = bc;
    let radio = entero(estilo.radio, 0, RADIO_PILDORA);
    if (radio !== null && radio > RADIO_MAX && radio !== RADIO_PILDORA) radio = null;
    if (radio !== null) out.radio = radio;
    if (typeof estilo.tamano === 'string' && Object.prototype.hasOwnProperty.call(TAMANOS, estilo.tamano)) {
        out.tamano = estilo.tamano;
    }
    return Object.keys(out).length ? out : null;
}

/** Une varios estilos de menos a más concreto: cada clave la decide el último que la trae. */
export function unirEstilos() {
    const out = {};
    for (let i = 0; i < arguments.length; i++) {
        const e = normalizarEstilo(arguments[i]);
        if (e) Object.keys(e).forEach(k => { out[k] = e[k]; });
    }
    return Object.keys(out).length ? out : null;
}

/** Etiqueta legible sobre un fondo (misma regla que `accentContrast` en `safe()`). */
export function etiquetaSobre(bgHex) {
    return getLuminance.apply(null, hexToRgb(bgHex)) > 0.179 ? '#111827' : '#ffffff';
}

/**
 * Las declaraciones CSS de un estilo (`--n3b-bg:#111111;…`), o '' si no hay nada que emitir.
 * Con fondo y sin texto, el texto se calcula (el «Automático» del panel). Con fondo, la sombra se tiñe
 * del fondo elegido (la de siempre se tiñe del acento).
 */
export function variablesDeEstilo(estilo) {
    const e = normalizarEstilo(estilo);
    if (!e) return '';
    let css = '';
    if (e.bg) {
        css += '--n3b-bg:' + e.bg + ';';
        css += '--n3b-shadow:0 10px 25px color-mix(in srgb, ' + e.bg + ' 30%, transparent);';
    }
    const fg = e.fg || (e.bg ? etiquetaSobre(e.bg) : '');
    if (fg) css += '--n3b-fg:' + fg + ';';
    if (e.borde !== undefined) {
        css += '--n3b-border:' + (e.borde === 0 ? 'none' : e.borde + 'px solid ' + (e.borde_color || fg || 'currentColor')) + ';';
    }
    if (e.radio !== undefined) css += '--n3b-radius:' + e.radio + 'px;';
    if (e.tamano) css += '--n3b-k:' + TAMANOS[e.tamano] + ';';
    return css;
}

/**
 * Lo que va en el `style` de UN botón: su sección (`props.boton`) + el propio (`botones[campo]` del
 * objeto dueño: el item de la tarjeta, o los props del bloque). '' si no hay nada.
 * @param {Object} props  props del bloque (crudas o de `safe()`: no lee colores).
 * @param {string} campo  el `data-field` del botón ('cta', 'submit_label').
 * @param {Object} [item] la tarjeta dueña del botón (un plan, una diapositiva).
 */
export function estiloDeBoton(props, campo, item) {
    const dueno = item || props;
    const propios = dueno && esObjetoPlano(dueno.botones) ? dueno.botones[campo] : null;
    let seccion = props && props.boton;
    if (CAMPOS_SECUNDARIOS.indexOf(campo) >= 0) seccion = soloForma(seccion);
    return variablesDeEstilo(unirEstilos(seccion, propios));
}

/** De un estilo, solo la forma (`CLAVES_DE_FORMA`), o null. */
export function soloForma(estilo) {
    const e = normalizarEstilo(estilo);
    if (!e) return null;
    const out = {};
    CLAVES_DE_FORMA.forEach(k => { if (e[k] !== undefined) out[k] = e[k]; });
    return Object.keys(out).length ? out : null;
}

/** Igual que `estiloDeBoton`, pero como atributo completo (` style="…"`) para un botón que no lleva. */
export function atributoDeBoton(props, campo, item) {
    const v = estiloDeBoton(props, campo, item);
    return v ? ' style="' + v + '"' : '';
}

/** Lo de toda la web (`site_tokens.boton`) como declaraciones, para el `:root` del publicado. */
export function variablesDeLaWeb(tokens) {
    return variablesDeEstilo(tokens && tokens.boton);
}

/**
 * Editor: pone (o quita) las variables de toda la web en un elemento (`#resultado`, la cabecera
 * izada). `setProperty` es API DOM: el valor no se interpola como markup.
 */
export function aplicarBotonWeb(el, tokens) {
    aplicarVariables(el, variablesDeLaWeb(tokens));
}

/**
 * Editor: deja en un elemento EXACTAMENTE las variables de unas declaraciones (`--n3b-bg:#111;…`) y quita
 * las demás `--n3b-*`. Lo usan lo de toda la web (`aplicarBotonWeb`) y la vista previa del panel «Estilo
 * del botón» sobre cada botón (con `estiloDeBoton`, lo mismo que escribiría el render).
 */
export function aplicarVariables(el, decl) {
    if (!el || !el.style) return;
    const puestas = {};
    String(decl || '').split(';').forEach(par => {
        const i = par.indexOf(':');
        if (i > 0) puestas[par.slice(0, i)] = par.slice(i + 1);
    });
    VARIABLES_BOTON.forEach(k => {
        if (puestas[k] !== undefined) el.style.setProperty(k, puestas[k]);
        else el.style.removeProperty(k);
    });
}
